import * as React from "react";

import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "@shared-ui/viewport-context";

import { UitkPrimaryButton, UitkSecondaryButton } from "uitk-react-button";
import { UitkSpacing, UitkSpacingProps } from "uitk-react-spacing";
import { UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkHeading, UitkText, UitkTextProps, UitkHeadingProps } from "uitk-react-text";
import { UitkLayoutFlexProps } from "uitk-react-layout-flex";
import { UitkLayoutConditionalGridTrack } from "uitk-react-layout-grid";

import { FTNodeOptions, ExtendedFTNode, ComponentProps } from "typings/flexFramework/FlexComponent";
import { EditorialFlexModule, FlexModuleResult } from "typings/microserviceModels/content-flex-module";

import { ScrimCard } from "components/shared/ScrimCard/ScrimCard";
import { ScrimCardTransparent } from "components/shared/ScrimCardTransparent/ScrimCardTransparent";
import { UitkScrimType } from "uitk-react-scrim";
import { FAILED_MODEL_RENDER } from "src/config/systemEvents";
import { NOOP_LOGGER as logger } from "bernie-logger";

import { FlexModuleModelStore } from "stores/FlexModuleModelStore";
import { observer } from "mobx-react";
import { normalizeOptionsData } from "src/components/flexFramework/FlexTemplateNode";
import { withStores } from "stores";
import { isVariantEnabled, Experiment } from "src/components/utility/experiment";
import { SpacingSize } from "src/components/utility/FlexAttributesUtil";
import EditorialBanner from "../EditorialBanner/EditorialBanner";

import { EGClickStreamTracker } from "src/components/utility/analytics/EGClickStream/EGClickStreamTracker";
import { TrackedVisibility } from "src/components/utility/analytics/TrackedVisibility";

export interface EditorialHeroBannerFTNodeOptions extends FTNodeOptions {}

export interface EditorialHeroBannerFTNode extends ExtendedFTNode {
  options: EditorialHeroBannerFTNodeOptions;
}

export interface EditorialBannerProps extends ComponentProps {
  flexTemplateNode: EditorialHeroBannerFTNode;
  flexModuleModelStore: FlexModuleModelStore;
}

interface SubtitleProps {
  subtitle: string;
  fontSize?: UitkTextProps["size"];
}

interface TitleProps {
  title: string;
  fontSize?: UitkHeadingProps["size"];
}

const Subtitle: React.FC<SubtitleProps> = ({ subtitle, fontSize = 500 }) => (
  <UitkText className="editorial-hero-heading" size={fontSize} theme="inverse">
    {subtitle}
  </UitkText>
);

const Title: React.FC<TitleProps> = ({ title, fontSize = 3 }) => (
  <UitkHeading className="editorial-hero-heading" size={fontSize}>
    {title}
  </UitkHeading>
);

export const EditorialHeroBanner = withStores("flexModuleModelStore")(
  observer((props: EditorialBannerProps) => {
    try {
      const { flexTemplateNode, flexModuleModelStore, context } = props;
      const { id, view } = flexTemplateNode;
      const model = flexModuleModelStore.getModel(id) as FlexModuleResult;
      const options = normalizeOptionsData(flexTemplateNode, model) as EditorialHeroBannerFTNodeOptions;
      const { fmId } = options;
      const isBrandRefreshJuneEnabled = isVariantEnabled(context, Experiment.EXPEDIA_BRAND_REFRESH_JUNE);
      const isBexHeroFullBleedEnabled = view === "editorial_banner-hero-v2";
      const className = "EditorialHeroBanner";

      /* istanbul ignore next*/
      if (!model) {
        return null;
      }

      // Re-use the existing EditorialBanner since it is almost the same logic
      if (isBexHeroFullBleedEnabled) {
        return (
          <TrackedVisibility rfrrId={className} moduleName={className}>
            <EditorialBanner {...props} isHero={true} />
          </TrackedVisibility>
        );
      }

      // @ts-ignore
      const [{ media, text }] = model.items;
      const { padding } = model as EditorialFlexModule;
      /**
       * This is a temporary work around due to limitations of the call-to-action model that will be addressed in the future.
       * Parsing data from contributed HTML content should not be considered an accepted approach to be repeated elsewhere
       */
      const htmlText = text as string;

      const [, title] = /<h3>(.*?)<\/h3>/g.exec(htmlText) || [];
      const [, subtitle] = /<p class="subtitle">(.*?)<\/p>/g.exec(htmlText) || [];
      const [, ctaLabel] = /<p><button>(.*?)<\/button><\/p>/g.exec(htmlText) || [];
      const [, subtitleTerm] = /<p class="subtitle-term">(.*?)<\/p>/g.exec(htmlText) || [];

      // Media content
      const [desktopMedia, mobileMedia] = media;
      const { mediaUrl: mobileMediaURL } = mobileMedia;
      const { clickUrl } = desktopMedia;

      // Component display properties
      const hasNoPadding = padding === SpacingSize.NONE;
      const spacing: UitkSpacingProps = { margin: { inline: "three", blockstart: hasNoPadding ? "unset" : "six" } };

      const justifyContent: UitkLayoutFlexProps["justifyContent"] = isBrandRefreshJuneEnabled
        ? "center"
        : "space-between";

      const mainGridColumns: UitkLayoutConditionalGridTrack = { small: 1, medium: 2 };

      const CtaButton = EGClickStreamTracker(isBrandRefreshJuneEnabled ? UitkSecondaryButton : UitkPrimaryButton);

      const scrimCardProps = {
        id: `editorial-banner-hero-${fmId}`,
        className,
        titleSlot: <Title title={title} />,
        subtitleSlot: <Subtitle subtitle={subtitle} />,
        footerSlot: (
          <UitkSpacing margin={{ blockend: "one" }}>
            <div>
              <UitkSpacing margin={{ block: "three" }}>
                <CtaButton
                  moduleName={className}
                  linkName="CTA button clicked"
                  rfrr={`home.${className}`}
                  tag="a"
                  href={clickUrl}
                >
                  {ctaLabel}
                </CtaButton>
              </UitkSpacing>
              {subtitleTerm && <Subtitle subtitle={subtitleTerm} fontSize={200} />}
            </div>
          </UitkSpacing>
        ),
        spacing,
        mainGridColumns,
        padding,
        justifyContent,
      };

      return (
        <Viewport>
          <ViewSmall>
            <ScrimCard
              {...scrimCardProps}
              titleSlot={<Title title={title} fontSize={4} />}
              subtitleSlot={<Subtitle subtitle={subtitle} fontSize={400} />}
              aspectRatio={UitkFigureAspectRatioType.R4_3}
              backgroundImageURL={mobileMediaURL}
              scrimType={UitkScrimType.OVERLAY}
              justifyContent={"start"}
              border={false}
            />
          </ViewSmall>
          <ViewMedium>
            <ScrimCardTransparent {...scrimCardProps} marginClass="s-t-padding-zero" />
          </ViewMedium>
          <ViewLarge>
            <ScrimCardTransparent {...scrimCardProps} marginClass="s-t-padding-zero" />
          </ViewLarge>
        </Viewport>
      );
    } catch (error) {
      logger.logEvent(FAILED_MODEL_RENDER, "Unexpected error occurred while rendering EditorialBanner module.", error);

      return null;
    }
  })
);

export default EditorialHeroBanner;
